import React, { useState, useEffect, useContext, useMemo } from "react";
import ReactSelect, { type StylesConfig } from "react-select";
import { Button, Field, TextInput, Select } from "../../../../ui";
import { FormGrid, Actions } from "./ui-pending";
import {
  EnterFirstName,
  EnterLastName,
  EmailJobPlaceholder,
  BirthDateJob,
  DelimiterPlaceholder,
} from "../../../../utils/Constant";
import { studentProfile, UpdateProfile, getUtilityTimezones, type UtilityTimezone } from "../../../../Api/apiUtils";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { toast } from "react-toastify";
import { MixpanelEvents,trackEvent } from "../../../../utils/mixpanelTracking";
import { isTeacherLeaderDisabled } from "../../../../utils/permissions";
import "./style.scss";
import "./panels.scss";

const stepTitles = ["Personal Details"];

interface ConfigType {
  states: string[];
  genders: { id: number; name: string }[];
  ageGroups: { id: number; name: string }[];
  countryCodes: { id: number; countryCode: string }[];
}

interface ProfileProps {
  studentId: string;
  credentialId: string;
  fetchProfile: () => void;
  studentProfileData:any;
  fetchCourses: () => Promise<void>;
  
}

const ProfileForm: React.FC<ProfileProps> = ({ studentId, credentialId, fetchProfile,fetchCourses, studentProfileData }) => {
  const readOnly = isTeacherLeaderDisabled();
  const { states = [], genders = [], ageGroups = [], countryCodes = [] } = useContext(ConfigContext) || {};
  const [timezones, setTimezones] = useState<UtilityTimezone[]>([]);
  const [timezonesLoading, setTimezonesLoading] = useState(true);

  const [activeStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loder, setLoder] = useState(false);
  const [originalData, setOriginalData] = useState<any>({});
  const [formData, setFormData] = useState<any>({
    firstName: "",
    lastName: "",
    gender: "",
    email: "",
    mobileNumber: "",
    countryCode: "",
    timeZone: "",
    state: "",
    ageGroup: "",
  });
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const loadTimezones = async () => {
      setTimezonesLoading(true);
      try {
        const data = await getUtilityTimezones();
        setTimezones(Array.isArray(data?.timezones) ? data.timezones : []);
      } catch {
        toast.error("Failed to load time zones.");
        setTimezones([]);
      } finally {
        setTimezonesLoading(false);
      }
    };
    void loadTimezones();
  }, []);

  useEffect(() => {
    if (studentProfileData) {
      const genderObj = genders.find((g: any) => g.name === studentProfileData.gender);
      const genderId = genderObj ? genderObj.id.toString() : "";

      const timezoneObj = timezones.find(
        (tz) =>
          tz.type === studentProfileData.timezone ||
          String(tz.id) === String(studentProfileData.timezone)
      );
      const timeZoneId = timezoneObj ? timezoneObj.id.toString() : "";

      const initialFormData = {
        userId: studentProfileData?.userId || "",
        firstName: studentProfileData?.firstName || "",
        lastName: studentProfileData?.lastName || "",
        gender: genderId,
        email: studentProfileData?.emailId || "",
        mobileNumber: studentProfileData?.mobileNumber || "",
        countryCode: studentProfileData?.countryCode || "",
        timeZone: timeZoneId,
        state: studentProfileData?.state || "",
        ageGroup: studentProfileData?.ageGroupId?.toString() || "",
      };

      setFormData(initialFormData);
      setOriginalData(initialFormData); // Store original data for comparison
      setIsLoading(false);
    }
  }, [studentProfileData, genders, timezones]);

  
  const validateForm = () => {
    const newErrors: any = {};
    
    // Basic required field validations
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    // if (!formData.lastName.trim()) {
    //   newErrors.lastName = "Last name is required";
    // }
    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    }
    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required";
    }
    if (!formData.countryCode) {
      newErrors.countryCode = "Country code is required";
    }
    if (!formData.timeZone) {
      newErrors.timeZone = "Time zone is required";
    }
    
    // Special validation for Indian timezones
    const selectedTimezone = timezones.find((tz: any) => tz.id.toString() === formData.timeZone);
    const timezoneName = selectedTimezone?.type?.toLowerCase() || "";
    const isIndianTimezone = timezoneName === "asia/kolkata" || timezoneName === "asia/calcutta";
    
    if (isIndianTimezone && !formData.state) {
      newErrors.state = "State is mandatory for Indian timezone";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (readOnly) {
      toast.error("Editing is disabled for Teacher Leaders");
      return;
    }
    console.log("formData.gender:", formData.gender);
    console.log("formData.timeZone:", formData.timeZone);
    console.log("formData:", formData);
    
    // Validate form before saving
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Find changed fields
    const changedFields: any = {};
    const fieldLabels: any = {
      firstName: "First Name",
      lastName: "Last Name", 
      gender: "Gender",
      email: "Email",
      mobileNumber: "Mobile Number",
      countryCode: "Country Code",
      timeZone: "Time Zone",
      state: "State",
      ageGroup: "Age Group"
    };

    const getDisplayValue = (key: string, value: any) => {
      if (!value) return "Empty";
      if (key === "timeZone") {
        const tz = timezones.find((tz: any) => tz.id.toString() === String(value));
        return tz?.type ?? tz?.name ?? value;
      }
      if (key === "gender") {
        const g = genders.find((g: any) => g.id.toString() === String(value));
        return g?.name ?? value;
      }
      if (key === "ageGroup") {
        const ag = ageGroups.find((ag: any) => ag.id.toString() === String(value));
        return ag?.name ?? value;
      }
      return value;
    };

    Object.keys(formData).forEach(key => {
      if (formData[key] !== originalData[key]) {
        changedFields[key] = {
          oldValue: getDisplayValue(key, originalData[key]),
          newValue: getDisplayValue(key, formData[key]),
          fieldLabel: fieldLabels[key] || key
        };
      }
    });

    const updateTimestamp = new Date().toISOString();
  
    const payload = {
      userId: Number(formData.userId),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      genderId: formData.gender ? parseInt(formData.gender) : null, 
      emailId: formData.email || null,
      mobileNumber: formData.mobileNumber || null,
      countryCode: formData.countryCode || null,
      timezone: formData.timeZone || null, 
      state: formData.state || null,
      birthDate: null,
      ageGroupId: formData.ageGroup ? parseInt(formData.ageGroup) : null,
      profilePhoto: null,
      socialHandles: {},
      interestTags: [],
    };

    // Track only changed fields with timestamp
    trackEvent(MixpanelEvents.STUDENT_PROFILE_UPDATE, {
      page: "Student Form Update",
      studentUserId: formData.userId,
      studentName: formData.firstName + " " + formData.lastName,
      updateTimestamp: updateTimestamp,
      changedFields: changedFields,
      totalFieldsChanged: Object.keys(changedFields).length,
      hasChanges: Object.keys(changedFields).length > 0});
    try {
      setLoder(true);
      const response = await UpdateProfile(payload);
      console.log("Profile updated successfully", response);
      toast.success("Profile updated successfully!");
      
      // Update original data after successful save
      setOriginalData(formData);
      
      fetchProfile();
      fetchCourses();
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoder(false);
    }
  };
  
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (readOnly) return;
    const { name, value } = e.target;

    if (name === "mobileNumber") {
      const maxDigits = String(formData.countryCode || "").trim() === "+91" ? 10 : 15;
      const digitsOnly = value.replace(/\D/g, "").slice(0, maxDigits);
      setFormData({ ...formData, mobileNumber: digitsOnly });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const countryCodeOptions = useMemo(
    () =>
      (countryCodes || []).map((code: any): { value: string; label: string } => ({
        value: String(code.countryCode ?? ""),
        label: String(code.countryCode ?? ""),
      })),
    [countryCodes]
  );

  const selectedCountryCodeOption =
    countryCodeOptions.find(
      (opt: { value: string; label: string }) =>
        opt.value === String(formData.countryCode || "")
    ) || null;

  const timezoneOptions = useMemo(
    () =>
      timezones.map((tz): { value: string; label: string } => ({
        value: String(tz.id),
        label: String(tz.type ?? ""),
      })),
    [timezones]
  );

  const selectedTimezoneOption =
    timezoneOptions.find(
      (opt: { value: string; label: string }) =>
        opt.value === String(formData.timeZone || "")
    ) || null;

  // react-select themed with Artium tokens; red border when the field has an error.
  const searchableSelectStyles = (hasError: boolean): StylesConfig<{ value: string; label: string }, false> => ({
    control: (base, state) => ({
      ...base,
      minHeight: "var(--ds-control-md)",
      borderRadius: "var(--ds-radius-sm)",
      borderColor: hasError
        ? "var(--ds-danger)"
        : state.isFocused
          ? "var(--ds-primary)"
          : "var(--ds-border-strong)",
      boxShadow: state.isFocused ? "var(--ds-focus-ring)" : "none",
      backgroundColor: state.isDisabled ? "var(--ds-disabled-bg)" : "var(--ds-surface)",
      fontSize: "14px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      border: "none",
      outline: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      color: "var(--ds-text)",
    }),
    singleValue: (base, state) => ({
      ...base,
      margin: 0,
      color: state.isDisabled ? "var(--ds-disabled-text)" : "var(--ds-text)",
    }),
    placeholder: (base) => ({ ...base, color: "var(--ds-text-subtle)" }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "var(--ds-radius-sm)",
      boxShadow: "var(--ds-shadow-lift)",
      backgroundColor: "var(--ds-surface)",
      color: "var(--ds-text)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--ds-primary)"
        : state.isFocused
          ? "var(--ds-primary-soft)"
          : "var(--ds-surface)",
      color: state.isSelected ? "var(--ds-text-on-dark)" : "var(--ds-text)",
    }),
  });

  const selectedTimezoneForState = timezones.find((tz: any) => tz.id.toString() === formData.timeZone);
  const stateTimezoneName = selectedTimezoneForState?.type?.toLowerCase() || "";
  const stateRequired = stateTimezoneName === "asia/kolkata" || stateTimezoneName === "asia/calcutta";

  const genderOptions = [
    { value: "", label: "Select Gender" },
    ...(genders || []).map((gender: any) => ({ value: gender.id.toString(), label: String(gender.name) })),
  ];
  const stateOptions = [
    { value: "", label: "Select State" },
    ...states.map((state: string) => ({ value: state, label: state })),
  ];
  const ageGroupOptions = [
    { value: "", label: "Select Age Group" },
    ...(ageGroups || []).map((ageGroup: any) => ({ value: String(ageGroup.id), label: String(ageGroup.name) })),
  ];

  return (
    <section className="profile-form" aria-labelledby="profile-form-title">
      <h3 id="profile-form-title" className="ds-card__title">
        {stepTitles[activeStep]}
      </h3>
      <form className="profile-form__form" onSubmit={(e) => e.preventDefault()}>
        <fieldset disabled={readOnly} className="profile-form__fieldset">
          {activeStep === 0 && (
            <FormGrid min={240}>
              <Field label="First name" required error={errors.firstName}>
                {(ctl) => (
                  <TextInput
                    {...ctl}
                    type="text"
                    placeholder={EnterFirstName}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                )}
              </Field>

              <Field label="Last name" required>
                {(ctl) => (
                  <TextInput
                    {...ctl}
                    type="text"
                    name="lastName"
                    placeholder={EnterLastName}
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                )}
              </Field>

              <Field label="Gender" required error={errors.gender}>
                {(ctl) => (
                  <Select
                    {...ctl}
                    name="gender"
                    options={genderOptions}
                    value={formData.gender}
                    onChange={handleInputChange}
                  />
                )}
              </Field>

              <Field label="Email" required error={errors.email} hint={readOnly ? "Hidden for Teacher Leaders" : undefined}>
                {(ctl) => (
                  <TextInput
                    {...ctl}
                    type="email"
                    name="email"
                    placeholder={EmailJobPlaceholder}
                    value={readOnly ? "••••@••••.com" : formData.email}
                    onChange={handleInputChange}
                    className={readOnly ? "profile-form__masked" : undefined}
                    readOnly={readOnly}
                    title={readOnly ? "Hidden for Teacher Leaders" : undefined}
                  />
                )}
              </Field>

              <div className="profile-form__phone">
                <Field label="Country code" required error={errors.countryCode} className="profile-form__phone-code">
                  {(ctl) =>
                    readOnly ? (
                      <Select
                        {...ctl}
                        options={[{ value: "", label: "••••" }]}
                        value=""
                        disabled
                        className="profile-form__masked"
                        title="Hidden for Teacher Leaders"
                      />
                    ) : (
                      <ReactSelect
                        inputId={ctl.id}
                        aria-describedby={ctl["aria-describedby"]}
                        aria-invalid={ctl["aria-invalid"]}
                        options={countryCodeOptions}
                        value={selectedCountryCodeOption}
                        onChange={(option) => {
                          if (readOnly) return;
                          const nextCode = option?.value || "";
                          const maxDigits = String(nextCode).trim() === "+91" ? 10 : 15;
                          const nextMobile = String(formData.mobileNumber || "")
                            .replace(/\D/g, "")
                            .slice(0, maxDigits);
                          setFormData({
                            ...formData,
                            countryCode: nextCode,
                            mobileNumber: nextMobile,
                          });
                          if (errors.countryCode) {
                            setErrors({ ...errors, countryCode: undefined });
                          }
                        }}
                        isSearchable
                        isClearable
                        placeholder="Code"
                        noOptionsMessage={() => "No match"}
                        filterOption={(option, rawInput) => {
                          const q = rawInput.trim().toLowerCase().replace(/\s+/g, "");
                          if (!q) return true;
                          const label = String(option.label).toLowerCase().replace(/\s+/g, "");
                          // Allow typing "91", "+91", "971", etc.
                          return (
                            label.includes(q) ||
                            label.replace(/^\+/, "").includes(q.replace(/^\+/, ""))
                          );
                        }}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        classNamePrefix="country-code-select"
                        styles={searchableSelectStyles(Boolean(errors.countryCode))}
                      />
                    )
                  }
                </Field>
                <Field label="Mobile number" required error={errors.mobileNumber} className="profile-form__phone-number">
                  {(ctl) => (
                    <TextInput
                      {...ctl}
                      type="tel"
                      inputMode="numeric"
                      placeholder={DelimiterPlaceholder}
                      name="mobileNumber"
                      value={readOnly ? "••••••••••••" : formData.mobileNumber}
                      onChange={handleInputChange}
                      maxLength={String(formData.countryCode || "").trim() === "+91" ? 10 : 15}
                      className={readOnly ? "profile-form__masked" : undefined}
                      readOnly={readOnly}
                      title={readOnly ? "Hidden for Teacher Leaders" : undefined}
                    />
                  )}
                </Field>
              </div>

              <Field label="Time zone" required error={errors.timeZone}>
                {(ctl) => (
                  <ReactSelect
                    inputId={ctl.id}
                    aria-describedby={ctl["aria-describedby"]}
                    aria-invalid={ctl["aria-invalid"]}
                    options={timezoneOptions}
                    value={selectedTimezoneOption}
                    onChange={(option) => {
                      if (readOnly) return;
                      setFormData({ ...formData, timeZone: option?.value || "" });
                      if (errors.timeZone) {
                        setErrors({ ...errors, timeZone: undefined });
                      }
                    }}
                    isDisabled={timezonesLoading || readOnly}
                    isSearchable
                    isClearable
                    placeholder={
                      timezonesLoading ? "Loading time zones..." : "Search or select time zone"
                    }
                    noOptionsMessage={() => "No matching time zone"}
                    filterOption={(option, rawInput) => {
                      const q = rawInput.trim().toLowerCase();
                      if (!q) return true;
                      const label = String(option.label).toLowerCase();
                      const city = label.includes("/") ? label.split("/").pop() || "" : label;
                      return (
                        label.includes(q) ||
                        city.includes(q) ||
                        label.replace(/[_/]/g, " ").includes(q)
                      );
                    }}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    classNamePrefix="timezone-select"
                    styles={searchableSelectStyles(Boolean(errors.timeZone))}
                  />
                )}
              </Field>

              <Field
                label="State"
                required={stateRequired}
                error={errors.state}
                hint={states.length > 0 ? undefined : "Loading states..."}
              >
                {(ctl) => (
                  <Select
                    {...ctl}
                    name="state"
                    options={stateOptions}
                    value={formData.state}
                    onChange={handleInputChange}
                  />
                )}
              </Field>

              <Field label="Age group">
                {(ctl) => (
                  <Select
                    {...ctl}
                    name="ageGroup"
                    options={ageGroupOptions}
                    value={formData.ageGroup}
                    onChange={handleInputChange}
                  />
                )}
              </Field>
            </FormGrid>
          )}
        </fieldset>
      </form>

      <Actions align="end">
        <Button
          variant="primary"
          onClick={handleSave}
          loading={loder}
          disabled={readOnly}
          title={readOnly ? "Disabled for Teacher Leaders" : undefined}
        >
          Save
        </Button>
      </Actions>
    </section>
  );
};

export default ProfileForm;